import React, { useState } from 'react'
import { Form } from '@inertiajs/react'
import { Eye, EyeOff } from 'lucide-react'
import { AuthLayout } from '@/layouts/AuthLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

type Props = { reset_password_token: string }

function ResetPasswordPage({ reset_password_token }: Props) {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)

  return (
    <Form method="put" action="/users/password">
      {({ errors, processing }: { errors: Record<string, string[]>; processing: boolean }) => (
        <div className="space-y-4">
          {/* Hidden token */}
          <input
            type="hidden"
            name="user[reset_password_token]"
            value={reset_password_token}
          />

          {/* New password field */}
          <div className="space-y-1.5">
            <Label htmlFor="user_password">New password</Label>
            <div className="relative">
              <Input
                id="user_password"
                name="user[password]"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="••••••••"
                className="pr-10"
                aria-invalid={!!errors['user[password]']}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            {errors['user[password]'] && (
              <p className="text-sm text-destructive mt-1">{errors['user[password]'][0]}</p>
            )}
          </div>

          {/* Password confirmation field */}
          <div className="space-y-1.5">
            <Label htmlFor="user_password_confirmation">Confirm new password</Label>
            <div className="relative">
              <Input
                id="user_password_confirmation"
                name="user[password_confirmation]"
                type={showConfirmation ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="••••••••"
                className="pr-10"
                aria-invalid={!!errors['user[password_confirmation]']}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                onClick={() => setShowConfirmation((prev) => !prev)}
                aria-label={showConfirmation ? 'Hide password' : 'Show password'}
              >
                {showConfirmation ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            {errors['user[password_confirmation]'] && (
              <p className="text-sm text-destructive mt-1">
                {errors['user[password_confirmation]'][0]}
              </p>
            )}
          </div>

          {/* Submit */}
          <Button type="submit" className="w-full" disabled={processing}>
            {processing ? 'Resetting...' : 'Reset password'}
          </Button>
        </div>
      )}
    </Form>
  )
}

ResetPasswordPage.layout = (page: React.ReactNode) => (
  <AuthLayout
    title="Set new password"
    description="Enter your new password below"
  >
    {page}
  </AuthLayout>
)

export default ResetPasswordPage
